'use client'
import { useCallback, useEffect, useState } from 'react'
import { useUser } from 'auth'
import type { NshiftReceiver, OrderAddressDTO, PostNordAddress } from 'ecosystem'
import {
  forgetRememberedAddress,
  forgetRememberedIdentity,
  GUEST_IDENTITY,
  readRememberedAddress,
  readRememberedAddressOptIn,
  reconcileRememberedAddressIdentity,
  type RememberedAddressKind,
  writeRememberedAddress,
  writeRememberedAddressOptIn
} from 'shared-utils'
import type { OrderAddressWithContact } from '../components/CheckoutPayment/DefaultDelivery/CustomAddressForm'

/**
 * Same-tab broadcast so the two or three pickers on one checkout page agree about the
 * opt-in. It is a single device-wide choice, but each form holds its own hook instance,
 * and without this a tick on the billing form would leave the shipping form's box
 * unticked until a reload.
 */
const OPT_IN_CHANGED_EVENT = 'checkout-address-opt-in-changed'

/**
 * What the shopper picked last time on this device, for the two pickers that offer a
 * choice at all.
 *
 * A *choice*, not an address. A B2B shopper's company addresses always outrank a bare
 * remembered address, so storing one meant their pick could never come back — the
 * checkbox promised something that never happened. Storing which option they took fixes
 * that, and it is the better record either way: a company pick is resolved against the
 * live `companyProfile.addresses` on every checkout, so editing the address in the
 * account is followed rather than shadowed by a stale copy.
 */
export type RememberedAddressChoice<T> =
  | { source: 'company'; companyAddressId: string }
  | { source: 'custom'; address: T }

/** What each form remembers. The shapes differ, which is why the buckets are separate. */
export interface RememberedAddressByKind {
  /** Address plus the contact details the Briqpay-style flows collect alongside it. */
  billing: RememberedAddressChoice<OrderAddressWithContact>
  shipping: RememberedAddressChoice<OrderAddressDTO>
  /** Carrier recipient forms offer no saved-address list, so these stay plain values. */
  postnord: PostNordAddress
  nshift: NshiftReceiver
}

export interface UseRememberedAddressResult<K extends RememberedAddressKind> {
  /**
   * What this device remembers, or `undefined` while the account is still settling, when
   * there is nothing stored, or when the shopper never opted in.
   *
   * The order always outranks it — a checkout being resumed already says where it is
   * going. Beyond that it is the shopper's own prior decision, so it outranks the
   * company *default*, which is only a guess at what they want.
   */
  remembered: RememberedAddressByKind[K] | undefined
  /**
   * Commit to the device. Call on save/valid, never on keystroke. A no-op until the
   * shopper has ticked the box, so call sites need no gate of their own.
   */
  remember: (value: RememberedAddressByKind[K]) => void
  /** Drop this form's memory for the current identity, leaving the other three alone. */
  forget: () => void
  /** Whether the shopper asked us to remember their details on this device. */
  isOptedIn: boolean
  /**
   * Bind to the "remember my details on this device" checkbox. Unticking withdraws the
   * permission and deletes everything it covered, not just this form's entry.
   */
  setOptedIn: (next: boolean) => void
  /**
   * True once the account has settled **and** the device has actually been read.
   *
   * Both halves matter. Anything that seeds an address must wait for this before
   * falling back to a company default, or it will install the default in the render
   * before `remembered` arrives and then skip the remembered choice forever, because
   * every seeding effect bails out once something is already selected.
   *
   * Writers of one-shot events (order provision) need it too: before the account
   * resolves there is no identity to file the choice under, so `remember` does nothing.
   */
  isReady: boolean
}

/**
 * Device memory for one checkout form, with the three things every call site would
 * otherwise have to repeat: identity namespacing, the opt-in gate and expiry.
 *
 * **Identity.** Entries are bucketed by customer id, or a single `guest` bucket when
 * nobody is signed in, and the buckets are reconciled on every mount — so a previous
 * account's address cannot surface for the next person at a shared machine. Nothing is
 * read until the account has settled: resolving to `guest` mid-flight would read as a
 * logout and wipe the signed-in shopper's own bucket on every page load.
 *
 * **Permission.** A name, street, postcode, email and phone are personal data, and
 * keeping them on someone's device for months is not strictly necessary to sell them
 * anything — so it happens only when they tick the box, which is the explicit request
 * ePrivacy Art. 5(3) asks for. Unticking deletes what was stored. Nothing
 * payment-related is ever kept.
 *
 * Deliberately *not* wired to the cookie banner: `packages/cookies` is mounted in no
 * storefront, and the CMP-backed `ConsentManager` in `data-collection` only exists in
 * the four apps that render `GTMProvider` and only reports a real state where a CMP is
 * configured. An explicit checkbox is the one mechanism that works on every store.
 */
export const useRememberedAddress = <K extends RememberedAddressKind>(
  kind: K
): UseRememberedAddressResult<K> => {
  type Value = RememberedAddressByKind[K]

  const { user, isLoading } = useUser()

  // Undefined until `useUser` settles — see the identity note above.
  const identity = isLoading ? undefined : (user?.customer?.id ?? GUEST_IDENTITY)

  const [remembered, setRemembered] = useState<Value | undefined>(undefined)
  const [isOptedIn, setIsOptedIn] = useState(false)
  const [isDeviceRead, setIsDeviceRead] = useState(false)

  useEffect(() => {
    if (!identity) return

    const read = () => {
      const optedIn = readRememberedAddressOptIn(identity)
      setIsOptedIn(optedIn)
      setRemembered(
        optedIn ? (readRememberedAddress<Value>(identity, kind) ?? undefined) : undefined
      )
      setIsDeviceRead(true)
    }

    reconcileRememberedAddressIdentity(identity)
    read()

    window.addEventListener(OPT_IN_CHANGED_EVENT, read)
    return () => window.removeEventListener(OPT_IN_CHANGED_EVENT, read)
  }, [identity, kind])

  const remember = useCallback(
    (value: Value) => {
      if (!identity || !isOptedIn) return
      // Skip a write that would only restate what is already stored. Call sites re-emit
      // the current selection on mount, and without this every visit to the checkout
      // would slide the 180-day expiry forward and the data would never age out.
      //
      // `JSON.stringify` is safe as an equality check here: each kind builds its value in
      // exactly one place, so key order is fixed, and `remembered` was parsed back from
      // what that same code wrote.
      if (JSON.stringify(remembered) === JSON.stringify(value)) return

      writeRememberedAddress(identity, kind, value)
      setRemembered(value)
    },
    [identity, isOptedIn, kind, remembered]
  )

  const forget = useCallback(() => {
    if (!identity) return
    forgetRememberedAddress(identity, kind)
    setRemembered(undefined)
  }, [identity, kind])

  const setOptedIn = useCallback(
    (next: boolean) => {
      if (!identity) return

      if (next) {
        writeRememberedAddressOptIn(identity)
      } else {
        forgetRememberedIdentity(identity)
        setRemembered(undefined)
      }

      setIsOptedIn(next)
      window.dispatchEvent(new CustomEvent(OPT_IN_CHANGED_EVENT))
    },
    [identity]
  )

  return {
    remembered,
    remember,
    forget,
    isOptedIn,
    setOptedIn,
    isReady: identity !== undefined && isDeviceRead
  }
}
